// @ts-check
import { trace, variation } from './boot-sector.js';
/** @typedef {import('./boot-sector.js').Palette} Palette */
/** Manufactured repeatable panels. @param {CanvasRenderingContext2D} c @param {number} x @param {number} y @param {number} w @param {number} h @param {Palette} p */
function panel(c, x, y, w, h, p) {
  const g = c.createLinearGradient(x, y, x + w, y + h);
  g.addColorStop(0, p.steel); g.addColorStop(0.035, p.metal); g.addColorStop(0.18, p.floor); g.addColorStop(1, p.void);
  c.fillStyle = g; c.fillRect(x, y, w, h); c.strokeStyle = p.wire; c.lineWidth = 1; c.strokeRect(x + 1, y + 1, w - 2, h - 2);
  c.strokeStyle = p.cyan; c.globalAlpha *= 0.5; trace(c, [[x + 10, y + h - 1], [x + w - 10, y + h - 1]]); c.globalAlpha /= 0.5;
}
/** Each system has a different construction, not just a palette.
 * @param {CanvasRenderingContext2D} c @param {number} world @param {number} index @param {number} w @param {number} h @param {number} layer @param {Palette} p
 */
export function paintWorldMachinery(c, world, index, w, h, layer, p) {
  c.clearRect(0, 0, w, h); c.save(); c.globalAlpha = layer ? 0.65 : 0.85;
  const top = layer ? h * 0.6 : h * 0.08, high = h - top;
  if (world === 2) {
    for (let lane = 0; lane < (layer ? 3 : 7); lane++) {
      const y = top + lane * high / 8;
      c.strokeStyle = lane % 2 ? p.violet : p.cyan; c.lineWidth = layer ? 2 : 3;
      trace(c, [[0, y + 30], [w * 0.3, y + 30], [w * 0.4, y], [w * 0.75, y], [w * 0.85, y + 30], [w, y + 30]]);
      c.globalAlpha = 0.15; c.lineWidth = 16; c.stroke(); c.globalAlpha = 0.7;
    }
    panel(c, w * 0.42, top, w * 0.25, high * 0.83, p);
    for (let row = 0; row < 8; row++) { c.strokeStyle = row % 2 ? p.cyan : p.violet; c.strokeRect(w * 0.45, top + 16 + row * high / 11, w * 0.19, high / 16); }
  } else if (world === 3) {
    for (let bank = 0; bank < 4; bank++) {
      const x = bank * w / 4 + 15, bw = w / 4 - 30;
      panel(c, x, top, bw, high, p);
      for (let row = 0; row < 13; row++) for (let col = 0; col < 5; col++) {
        const missing = variation(index * 97 + row * 5 + col, bank) > 0.8;
        c.globalAlpha = missing ? 0.13 : 0.48; c.fillStyle = missing ? p.wire : (row + col) % 5 ? p.cyan : p.violet;
        c.fillRect(x + 10 + col * (bw - 20) / 5, top + 14 + row * (high - 28) / 13, (bw - 30) / 5, (high - 48) / 13);
      }
      c.globalAlpha = 0.7;
    }
  } else if (world === 4) {
    for (let divider = 0; divider < 5; divider++) {
      const x = 30 + divider * w / 5;
      panel(c, x, top, 44, high, p);
      for (let mark = 0; mark < 16; mark++) {
        c.fillStyle = mark % 4 ? p.wire : p.cyan; c.fillRect(x + 10, top + mark * high / 17 + 8, mark % 4 ? 12 : 24, 2);
      }
      c.strokeStyle = divider % 2 ? p.violet : p.cyan; c.lineWidth = 2;
      const y = top + 30 + divider * high / 7;
      trace(c, [[x + 44, y], [x + 75, y], [x + 75, y + 36], [x + 115, y + 36], [x + 115, y], [x + w / 5, y]]);
    }
  } else if (world === 5) {
    for (let structure = 0; structure < 4; structure++) {
      const x = structure * w / 4 + 16, y = top + (structure % 2) * high * 0.2, sw = w / 4 - 44, sh = high * 0.65;
      if (structure % 2) panel(c, x, y, sw, sh, p);
      c.strokeStyle = structure % 2 ? p.steel : p.cyan; c.lineWidth = 1;
      for (let row = 0; row <= 8; row++) trace(c, [[x, y + row * sh / 8], [x + sw, y + row * sh / 8], [x + sw + 28, y + row * sh / 8 - 28]]);
      for (let col = 0; col <= 5; col++) trace(c, [[x + col * sw / 5, y + sh], [x + col * sw / 5, y], [x + col * sw / 5 + 28, y - 28]]);
      trace(c, [[x + 28, y - 28], [x + sw + 28, y - 28], [x + sw + 28, y + sh - 28]]);
    }
  } else if (world === 6) {
    for (let fin = 0; fin < 24; fin++) {
      const x = fin * w / 24, y = top + Math.abs(12 - fin) * 3;
      panel(c, x, y, w / 24 - 7, h - y, p);
      c.fillStyle = p.cyan; c.globalAlpha = 0.23; c.fillRect(x + 4, y + 8, 3, h - y - 12); c.globalAlpha = 0.8;
    }
    if (!layer) { panel(c, w * 0.34, h * 0.25, w * 0.3, h * 0.5, p); c.strokeStyle = p.violet; c.lineWidth = 10; c.strokeRect(w * 0.38, h * 0.31, w * 0.22, h * 0.38); }
  } else if (world === 7) {
    for (let shield = 0; shield < 4; shield++) {
      const x = shield * w / 4 + 20, sw = w / 4 - 40;
      panel(c, x, top, sw, high, p);
      c.strokeStyle = p.steel; c.lineWidth = 3; trace(c, [[x + 12, h - 12], [x + 12, top + 22], [x + sw - 12, top + 22], [x + sw - 12, h - 12]]);
      c.strokeStyle = p.cyan; c.lineWidth = 1; trace(c, [[x + 30, top + high * 0.3], [x + sw / 2, top + high * 0.22], [x + sw - 30, top + high * 0.3], [x + sw - 30, top + high * 0.52], [x + sw / 2, top + high * 0.65], [x + 30, top + high * 0.52], [x + 30, top + high * 0.3]]);
    }
  } else {
    const cx = w / 2, cy = top + high * 0.48, radius = Math.min(w * 0.33, high * 0.45);
    for (let ring = 3; ring >= 0; ring--) {
      const r = radius * (0.4 + ring * 0.2); c.beginPath();
      for (let n = 0; n < 8; n++) { const a = Math.PI / 8 + n * Math.PI / 4; const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r; if (n) c.lineTo(x, y); else c.moveTo(x, y); }
      c.closePath(); c.fillStyle = ring % 2 ? p.metal : p.void; c.fill(); c.strokeStyle = ring % 2 ? p.cyan : p.steel; c.lineWidth = 2; c.stroke();
    }
    for (let bus = 0; bus < 12; bus++) { c.strokeStyle = bus % 2 ? p.cyan : p.violet; c.lineWidth = 1; trace(c, [[0, top + bus * high / 12], [cx - radius - 24, top + bus * high / 12], [cx - radius * 0.65, cy - 44 + bus * 8]]); trace(c, [[cx + radius * 0.65, cy - 44 + bus * 8], [cx + radius + 24, top + bus * high / 12], [w, top + bus * high / 12]]); }
  }
  c.restore();
}
/** Small dynamic pass over cached architecture. @param {CanvasRenderingContext2D} c @param {number} world @param {number} index @param {number} w @param {number} h @param {number} time @param {number} energy @param {Palette} p */
export function paintWorldSignals(c, world, index, w, h, time, energy, p) {
  c.save(); c.globalAlpha = 0.4 + energy * 0.4; c.fillStyle = p.cyan; c.strokeStyle = p.cyan;
  if (world === 2 || world === 8) {
    for (let lane = 0; lane < 7; lane++) { const x = ((time * (lane % 2 ? -90 : 100) + lane * 131 + index * 53) % w + w) % w; c.fillStyle = lane % 2 ? p.violet : p.cyan; c.fillRect(x, h * 0.08 + lane * h * 0.92 / 8 + 29, 20, 3); }
  } else if (world === 3 || world === 5 || world === 7) {
    const y = h * 0.08 + (time * (world === 7 ? 0.11 : 0.07) % 1) * h * 0.85;
    const glow = c.createLinearGradient(0, y - 26, 0, y + 4); glow.addColorStop(0, 'transparent'); glow.addColorStop(1, p.cyan);
    c.globalAlpha = 0.13 + energy * 0.1; c.fillStyle = glow; c.fillRect(0, y - 26, w, 30); c.globalAlpha = 0.5; c.fillStyle = p.cyan; c.fillRect(0, y + 3, w, 1);
  } else if (world === 4) {
    for (let i = 0; i < 5; i++) { c.globalAlpha = Math.floor(time * 2 + i) % 3 === 0 ? 0.8 : 0.12; c.fillRect(40 + i * w / 5, h * 0.08 + 6, 24, 8); }
  } else if (world === 6) {
    const cx = w * 0.49, cy = h * 0.5; c.translate(cx, cy); c.rotate(time * 0.6); c.strokeStyle = p.violet;
    const radius = Math.min(80, h * 0.17);
    for (let blade = 0; blade < 8; blade++) {
      c.rotate(Math.PI / 4); c.fillStyle = p.steel; c.globalAlpha = 0.48;
      c.beginPath(); c.moveTo(12, -4); c.lineTo(radius * 0.7, -radius * 0.32); c.lineTo(radius, -radius * 0.15); c.lineTo(radius * 0.82, 8); c.lineTo(15, 9); c.closePath(); c.fill(); c.stroke();
    }
    c.globalAlpha = 1; c.fillStyle = p.metal; c.beginPath(); c.arc(0, 0, 15, 0, Math.PI * 2); c.fill(); c.stroke();
  }
  c.restore();
}
